import React, { useState, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, ChevronUp, Users, Calendar, Clock, Search, CalendarDays, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Employee, DayShift, ShiftTemplate, DayTemplate, BusinessOpeningHour } from '@/types';
import DraggableEmployeeCard from '@/components/shifts/DraggableEmployeeCard';
import ShiftTemplateItem from '@/components/shifts/ShiftTemplateItem';
import DayTemplateApplicator from '@/components/shifts/DayTemplateApplicator';
import DayGroupActions from '@/components/shifts/DayGroupActions';
import ShiftEditingModePanel from '@/components/shifts/ShiftEditingModePanel';
import { useRoles } from '@/hooks/employees/useRoles';
import { usePlanFulfillment } from '@/hooks/employees/usePlanFulfillment';
import { cn } from '@/lib/core/utils';

export type UnifiedActionsPanelMode = 'employees' | 'day-actions' | 'shift-templates-editing';

interface UnifiedActionsPanelProps {
  // Mode selection
  selectedMode: UnifiedActionsPanelMode;
  onModeChange: (mode: UnifiedActionsPanelMode) => void;
  
  // Business context
  businessId: string;
  selectedDate?: Date;
  
  // Employee data
  employees: Employee[];
  allDayShifts: DayShift[];
  onEmployeeDragStart?: (employee: Employee) => void;
  onEmployeeDragEnd?: () => void;
  
  // Template data
  shiftTemplates: ShiftTemplate[];
  dayTemplates: DayTemplate[];
  loadingShiftTemplates: boolean;
  loadingDayTemplates: boolean;
  
  // Template actions
  onEditShiftTemplate: (templateId: string) => void;
  onDeleteShiftTemplate: (templateId: string) => Promise<void>;
  onDeleteDayTemplate: (templateId: string) => void;
  onApplyDayTemplate: (template: DayTemplate, targetDate: Date, replace: boolean) => void;
  
  // Panel configuration
  className?: string;
  onToggleCollapsed?: () => void;

  // Props for day template application prompt
  selectedTemplateForApplication: DayTemplate | null;
  targetDateForTemplateApplication: Date | null;
  isTemplatePopoverOpen: boolean;
  onTemplatePopoverOpenChange: (isOpen: boolean) => void;
  onClearSelectedTemplate: () => void;
  
  // Props for group actions
  selectedEditingDate?: Date | null;
  isEditingMode?: boolean;
  onDaySelect?: (date: Date) => void;
  onClearAllShifts?: (date: Date) => Promise<void>;
  onShiftCreated?: () => void;
  onSaveDayTemplate?: (name: string, description: string) => Promise<void>;
  
  // Props for shift editing mode
  selectedShiftIds?: Set<string>;
  onShiftSelect?: (shiftId: string, selected: boolean) => void;
  onSelectAllShifts?: () => void;
  onClearShiftSelection?: () => void;
  onEditShift?: (shiftId: string) => void;
  onDeleteShift?: (shiftId: string) => void;
  onCopyShifts?: (shiftIds: string[]) => void;
  onSaveShiftsAsTemplate?: (shiftIds: string[], name: string, description?: string) => void;

  // Opening hours for closed date display (DayGroupActions now uses context directly)
  openingHours?: BusinessOpeningHour[] | null;
}

const UnifiedActionsPanel: React.FC<UnifiedActionsPanelProps> = ({
  selectedMode,
  onModeChange,
  businessId,
  selectedDate,
  employees,
  allDayShifts,
  onEmployeeDragStart,
  onEmployeeDragEnd,
  shiftTemplates,
  dayTemplates,
  loadingShiftTemplates,
  loadingDayTemplates,
  onEditShiftTemplate,
  onDeleteShiftTemplate,
  onEditDayTemplate,
  onDeleteDayTemplate,
  onApplyDayTemplate,
  className = "",
  onToggleCollapsed,
  selectedTemplateForApplication,
  targetDateForTemplateApplication,
  isTemplatePopoverOpen,
  onTemplatePopoverOpenChange,
  onClearSelectedTemplate,
  selectedEditingDate,
  isEditingMode,
  onDaySelect,
  onClearAllShifts,
  onShiftCreated,
  onSaveDayTemplate,
  selectedShiftIds,
  onShiftSelect,
  onSelectAllShifts,
  onClearShiftSelection,
  onEditShift,
  onDeleteShift,
  onCopyShifts,
  onSaveShiftsAsTemplate,
  // Opening hours for closed date display
  openingHours,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);
  const [selectedFulfillmentStatuses, setSelectedFulfillmentStatuses] = useState<Set<string>>(new Set());
  const [isFulfillmentFilterOpen, setIsFulfillmentFilterOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch roles for the business
  const { data: roles = [], isLoading: rolesLoading } = useRoles(businessId);

  // Fetch plan fulfillment data for all employees
  const employeeIds = useMemo(() => employees.map(e => e.id), [employees]);
  const { fulfillmentData } = usePlanFulfillment(businessId, employeeIds);

  // Fulfillment status options for filtering
  const fulfillmentStatusOptions = useMemo(() => [
    { id: 'ahead', label: 'Ahead', color: 'text-green-600' },
    { id: 'on_track', label: 'On Track', color: 'text-emerald-600' },
    { id: 'behind', label: 'Behind', color: 'text-amber-600' },
    { id: 'significantly_behind', label: 'Very Behind', color: 'text-red-600' },
    { id: 'no_target', label: 'No Target', color: 'text-muted-foreground' },
  ], []);

  // Handle role selection toggle
  const toggleRoleSelection = useCallback((roleId: string) => {
    setSelectedRoleIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  }, []);

  // Clear all role filters
  const clearRoleFilters = useCallback(() => {
    setSelectedRoleIds(new Set());
  }, []);

  // Handle fulfillment status selection toggle
  const toggleFulfillmentStatus = useCallback((statusId: string) => {
    setSelectedFulfillmentStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(statusId)) {
        newSet.delete(statusId);
      } else {
        newSet.add(statusId);
      }
      return newSet;
    });
  }, []);

  // Clear all fulfillment filters
  const clearFulfillmentFilters = useCallback(() => {
    setSelectedFulfillmentStatuses(new Set());
  }, []);

  // Use props for selected day template, instead of internal state
  const selectedDayTemplate = useMemo(() => {
    if (isTemplatePopoverOpen && selectedTemplateForApplication && targetDateForTemplateApplication) {
      return { template: selectedTemplateForApplication, targetDate: targetDateForTemplateApplication };
    }
    return null;
  }, [isTemplatePopoverOpen, selectedTemplateForApplication, targetDateForTemplateApplication]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case '/':
        if (searchInputRef.current && event.ctrlKey) {
          event.preventDefault();
          searchInputRef.current.focus();
        }
        break;
      case 'e':
        if (event.ctrlKey) {
          event.preventDefault();
          onModeChange('employees');
        }
        break;
      case 'g':
        if (event.ctrlKey) {
          event.preventDefault();
          onModeChange('day-actions');
        }
        break;
      case 't':
        if (event.ctrlKey && event.shiftKey) {
          event.preventDefault();
          onModeChange('shift-templates-editing');
        }
        break;
    }
  }, [onModeChange]);

  const handleApplyDayTemplate = useCallback((replace: boolean) => {
    if (selectedTemplateForApplication && targetDateForTemplateApplication) {
      onApplyDayTemplate(selectedTemplateForApplication, targetDateForTemplateApplication, replace);
      onClearSelectedTemplate();
      onTemplatePopoverOpenChange(false);
    }
  }, [selectedTemplateForApplication, targetDateForTemplateApplication, onApplyDayTemplate, onClearSelectedTemplate, onTemplatePopoverOpenChange]);

  const handleCancelApplyDayTemplate = useCallback(() => {
    onClearSelectedTemplate();
    onTemplatePopoverOpenChange(false);
  }, [onClearSelectedTemplate, onTemplatePopoverOpenChange]);

  // Get current items and metadata based on selected mode
  const { currentItems, isLoading, emptyMessage } = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    switch (selectedMode) {
      case 'employees': {
        let filtered = employees;

        // Filter by roles if any are selected
        if (selectedRoleIds.size > 0) {
          filtered = filtered.filter(employee =>
            employee.role_id && selectedRoleIds.has(employee.role_id)
          );
        }

        // Filter by fulfillment status if any are selected
        if (selectedFulfillmentStatuses.size > 0) {
          filtered = filtered.filter(employee => {
            const fulfillment = fulfillmentData.get(employee.id);
            const status = fulfillment?.status || 'no_target';
            return selectedFulfillmentStatuses.has(status);
          });
        }

        // Filter by search term
        if (term) {
          filtered = filtered.filter(employee =>
            employee.name.toLowerCase().includes(term) ||
            (employee.roleInfo?.name || '').toLowerCase().includes(term)
          );
        }

        return {
          currentItems: filtered.map(employee => ({
            id: employee.id,
            type: 'employee' as const,
            data: employee
          })),
          isLoading: false,
          emptyMessage: employees.length === 0 ? 'No employees available' : 'No employees match your filters'
        };
      }
      
      
      
      case 'day-actions': {
        // For day actions, we don't need to show items in the list
        // The component will be rendered separately
        return {
          currentItems: [],
          isLoading: false,
          emptyMessage: 'Day actions for day operations'
        };
      }
      
      
      case 'shift-templates-editing': {
        // For shift-templates-editing, we'll render a custom layout
        return {
          currentItems: [],
          isLoading: false,
          emptyMessage: 'Templates and editing combined'
        };
      }
      
      default:
        return {
          currentItems: [],
          isLoading: false,
          emptyMessage: 'No items available'
        };
    }
  }, [selectedMode, searchTerm, selectedRoleIds, selectedFulfillmentStatuses, fulfillmentData, employees, shiftTemplates, dayTemplates, loadingShiftTemplates, loadingDayTemplates]);

  // Render individual item based on type
  const renderItem = useCallback((item: typeof currentItems[0]) => {
    switch (item.type) {
      case 'employee':
        return (
          <DraggableEmployeeCard
            key={item.id}
            employee={item.data}
            fulfillment={fulfillmentData.get(item.id)}
            onDragStart={onEmployeeDragStart}
            onDragEnd={onEmployeeDragEnd}
          />
        );

      case 'shift-template':
        return (
          <ShiftTemplateItem
            key={item.id}
            template={item.data}
            compact={true}
            onEdit={onEditShiftTemplate}
            onDelete={onDeleteShiftTemplate}
          />
        );


      default:
        return null;
    }
  }, [fulfillmentData, onEmployeeDragStart, onEmployeeDragEnd, onEditShiftTemplate, onDeleteShiftTemplate]);

  if (isTemplatePopoverOpen && selectedDayTemplate) {
    return (
      <DayTemplateApplicator
        template={selectedDayTemplate.template}
        targetDate={selectedDayTemplate.targetDate}
        onApply={handleApplyDayTemplate}
        onCancel={handleCancelApplyDayTemplate}
        hasExistingShifts={allDayShifts.some(shift => new Date(shift.start_time).toDateString() === selectedDayTemplate.targetDate.toDateString())}
      />
    );
  }

  return (
    <div
      className={`w-36 max-w-36 h-full flex flex-col bg-transparent ${className}`}
      role="complementary"
      aria-label={`${selectedMode.replace('-', ' ')} panel`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      data-tutorial="actions-panel"
    >
      {/* Fixed header */}
      <div className="p-2 space-y-2 flex-shrink-0 max-w-full">
        {/* Mode selector */}
        <Select
          value={selectedMode}
          onValueChange={onModeChange}
          aria-label="Select content mode"
        >
          <SelectTrigger className="h-7 text-xs !bg-background w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employees" className="relative">
              <div className="flex items-center gap-1 text-xs">
                Employees
              </div>
            </SelectItem>
            <SelectItem value="day-actions" className="relative">
              <div className="flex items-center gap-1 text-xs">
                Day Actions
              </div>
            </SelectItem>
            <SelectItem value="shift-templates-editing" className="relative">
              <div className="flex items-center gap-1 text-xs">
                Shifts
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Search input - hidden for day actions and shift-templates-editing */}
        {selectedMode !== 'day-actions' && selectedMode !== 'shift-templates-editing' && (
          <div className="relative">
            <Input
              ref={searchInputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-6 text-xs !bg-background w-full"
              placeholder="Search..."
              aria-label={`Search ${selectedMode.replace('-', ' ')}`}
              title="Use Ctrl+/ to focus search"
            />
            <Search className="h-3 w-3 absolute right-2 top-1.5 text-muted-foreground" aria-hidden="true" />
          </div>
        )}

        {/* Role filter - only shown in employees mode */}
        {selectedMode === 'employees' && (
          <Popover open={isRoleFilterOpen} onOpenChange={setIsRoleFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={
                  selectedRoleIds.size > 0
                    ? 'w-full h-8 justify-between border-2 border-primary dark:border-primary'
                    : 'w-full h-8 justify-around'
                }
                aria-label="Filter by role"
              >
                <span className="flex items-center truncate" style={{gap: '0.25rem'}}>
                  <Users className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                  {(() => {
                    if (selectedRoleIds.size === 0) {
                      return 'Roles';
                    }
                    if (selectedRoleIds.size === 1) {
                      const selectedRole = roles.find(r => selectedRoleIds.has(r.id));
                      return <span className="truncate text-xs">{selectedRole?.name || 'Role'}</span>;
                    }
                    return <span className="truncate text-xs">{selectedRoleIds.size} roles</span>;
                  })()}
                </span>
                {selectedRoleIds.size > 0 && (
                  <div
                    className="relative z-10 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-800/30 rounded p-1 flex-shrink-0 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearRoleFilters();
                    }}
                    aria-hidden="true"
                  >
                    <X className="h-4 w-4 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                  </div>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              className="w-auto p-0"
              sideOffset={4}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="p-3">
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Roles
                      {selectedRoleIds.size > 0 && (
                        <span className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5 rounded-full">
                          {selectedRoleIds.size}
                        </span>
                      )}
                    </h4>
                  </div>

                  {/* All/None buttons */}
                  <div className="flex justify-center" style={{gap: '0.25rem'}}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs flex-1"
                      onClick={() => {
                        const allRoleIds = new Set(roles.map(r => r.id));
                        setSelectedRoleIds(allRoleIds);
                      }}
                    >
                      All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs flex-1"
                      onClick={clearRoleFilters}
                    >
                      None
                    </Button>
                  </div>

                  {/* Role checkboxes */}
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {roles.map(role => {
                      // Count employees with this role
                      const employeeCount = employees.filter(e => e.role_id === role.id).length;

                      return (
                        <div key={role.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`role-${role.id}`}
                            checked={selectedRoleIds.has(role.id)}
                            onCheckedChange={() => toggleRoleSelection(role.id)}
                          />
                          <label
                            htmlFor={`role-${role.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer flex-1"
                          >
                            <span className="truncate">{role.name} ({employeeCount})</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Fulfillment status filter - only shown in employees mode */}
        {selectedMode === 'employees' && (
          <Popover open={isFulfillmentFilterOpen} onOpenChange={setIsFulfillmentFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={
                  selectedFulfillmentStatuses.size > 0
                    ? 'w-full h-8 justify-between border-2 border-primary dark:border-primary'
                    : 'w-full h-8 justify-around'
                }
                aria-label="Filter by plan progress"
              >
                <span className="flex items-center truncate" style={{gap: '0.25rem'}}>
                  <Clock className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                  {(() => {
                    if (selectedFulfillmentStatuses.size === 0) {
                      return 'Progress';
                    }
                    if (selectedFulfillmentStatuses.size === 1) {
                      const selectedStatus = fulfillmentStatusOptions.find(s => selectedFulfillmentStatuses.has(s.id));
                      return <span className="truncate text-xs">{selectedStatus?.label || 'Status'}</span>;
                    }
                    return <span className="truncate text-xs">{selectedFulfillmentStatuses.size} statuses</span>;
                  })()}
                </span>
                {selectedFulfillmentStatuses.size > 0 && (
                  <div
                    className="relative z-10 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-800/30 rounded p-1 flex-shrink-0 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearFulfillmentFilters();
                    }}
                    aria-hidden="true"
                  >
                    <X className="h-4 w-4 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                  </div>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              className="w-auto p-0"
              sideOffset={4}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="p-3">
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Plan Progress
                      {selectedFulfillmentStatuses.size > 0 && (
                        <span className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5 rounded-full">
                          {selectedFulfillmentStatuses.size}
                        </span>
                      )}
                    </h4>
                  </div>

                  {/* All/None buttons */}
                  <div className="flex justify-center" style={{gap: '0.25rem'}}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs flex-1"
                      onClick={() => {
                        const allStatusIds = new Set(fulfillmentStatusOptions.map(s => s.id));
                        setSelectedFulfillmentStatuses(allStatusIds);
                      }}
                    >
                      All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs flex-1"
                      onClick={clearFulfillmentFilters}
                    >
                      None
                    </Button>
                  </div>

                  {/* Status checkboxes */}
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {fulfillmentStatusOptions.map(status => {
                      // Count employees with this status
                      const employeeCount = employees.filter(e => {
                        const fulfillment = fulfillmentData.get(e.id);
                        return (fulfillment?.status || 'no_target') === status.id;
                      }).length;

                      return (
                        <div key={status.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`status-${status.id}`}
                            checked={selectedFulfillmentStatuses.has(status.id)}
                            onCheckedChange={() => toggleFulfillmentStatus(status.id)}
                          />
                          <label
                            htmlFor={`status-${status.id}`}
                            className={cn(
                              "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer flex-1",
                              status.color
                            )}
                          >
                            <span className="truncate">{status.label} ({employeeCount})</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Scrollable content */}
      <ScrollArea className="flex-1 max-w-full overflow-hidden">
        {selectedMode === 'shift-templates-editing' ? (
          /* 60/40 Split Layout - constrained to 9rem width */
          <div className="h-full flex flex-col max-w-[9rem] w-full">
            {/* Top section - Shift Templates (min 50% height) */}
            <div className="min-h-[50%] flex-1 border-b border-border/40 p-2 " data-tutorial="shift-templates">
              <div className="text-xs font-medium text-muted-foreground mb-1 px-1 truncate">
                Templates
              </div>
              <div className="relative mb-1">
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-5 pr-6 text-xs !bg-background"
                  placeholder="Search..."
                  aria-label="Search shift templates"
                />
                <Search className="h-3 w-3 absolute right-1.5 top-1 text-muted-foreground" />
              </div>
              <ScrollArea className="h-[calc(100%-40px)]">
                {loadingShiftTemplates ? (
                  <div className="flex items-center justify-center py-2">
                    <div className="text-xs text-gray-500">Loading...</div>
                  </div>
                ) : shiftTemplates.length === 0 ? (
                  <div className="text-xs text-gray-500 py-2 text-center">No templates</div>
                ) : (
                  <div className="space-y-1 pr-1">
                    {shiftTemplates
                      .filter(template =>
                        !searchTerm.trim() ||
                        template.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
                      )
                      .map(template => (
                        <div key={template.id} className="max-w-full overflow-hidden">
                          <ShiftTemplateItem
                            template={template}
                            compact={true}
                            onEdit={onEditShiftTemplate}
                            onDelete={onDeleteShiftTemplate}
                          />
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Bottom section - Shift Operations (min 50% height) */}
            <div className="min-h-[50%] flex-1">
              <div className="text-xs font-medium text-muted-foreground mb-1 px-1 truncate">
                Operations
              </div>
              <div className="h-[calc(100%-16px)] max-w-[9rem] overflow-hidden">
                <ShiftEditingModePanel
                  businessId={businessId}
                  selectedDate={selectedDate}
                  allDayShifts={allDayShifts}
                  employees={employees}
                  selectedShiftIds={selectedShiftIds || new Set()}
                  onShiftSelect={onShiftSelect || (() => {})}
                  onSelectAll={onSelectAllShifts || (() => {})}
                  onClearSelection={onClearShiftSelection || (() => {})}
                  onEditShift={onEditShift || (() => {})}
                  onDeleteShift={onDeleteShift || (() => {})}
                  onCopyShifts={onCopyShifts || (() => {})}
                  onSaveAsTemplate={onSaveShiftsAsTemplate || (() => {})}
                  onShiftCreated={onShiftCreated}
                  className="h-full max-w-full"
                  isTemplatesEditingMode={true}
                />
              </div>
            </div>
          </div>
        ) : selectedMode === 'day-actions' ? (
          <div className="pb-2 max-w-full overflow-hidden" data-tutorial="day-templates">
            <DayGroupActions
              businessId={businessId}
              selectedDate={selectedDate || new Date()}
              selectedEditingDate={selectedEditingDate}
              isEditingMode={isEditingMode}
              allDayShifts={allDayShifts}
              onDaySelect={onDaySelect}
              onClearAllShifts={onClearAllShifts}
              onShiftCreated={onShiftCreated}
              onSaveDayTemplate={onSaveDayTemplate}
              dayTemplates={dayTemplates || []}
              loadingDayTemplates={loadingDayTemplates || false}
              onEditDayTemplate={onEditDayTemplate || (() => {})}
              onDeleteDayTemplate={onDeleteDayTemplate || (() => {})}
              onApplyDayTemplate={onApplyDayTemplate}
              openingHours={openingHours}
            />
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-xs text-gray-500">
              Loading {selectedMode.replace('-', ' ')}...
            </div>
          </div>
        ) : currentItems.length === 0 ? (
          <div className="text-xs text-gray-500 py-4 text-center">
            {emptyMessage}
          </div>
        ) : (
          <div className="space-y-2 p-2 max-w-full" data-tutorial="employee-list">
            {currentItems.map(renderItem)}
          </div>
        )}
      </ScrollArea>
      
      {/* Hidden help text for screen readers */}
      <div className="sr-only">
        <div>Use Ctrl+E for employees, Ctrl+G for day actions, Ctrl+Shift+T for templates + editing</div>
        <div>Use Ctrl+/ to focus search</div>
      </div>
    </div>
  );
};

export default UnifiedActionsPanel;